import React, { useState, useEffect } from 'react';
import { CreditCard, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import api from '@/utils/api';

const Payments = () => {
  const [salaries, setSalaries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSalary, setSelectedSalary] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('Bank Transfer');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const response = await api.get('/salary-process-v2?source=all');
      const approvedSalaries = response.data.filter(
        s => s.ceo_status === 'Approved' && s.payment_status !== 'Paid'
      );
      setSalaries(approvedSalaries);
    } catch (error) {
      toast.error('Failed to load payments');
    } finally {
      setLoading(false);
    }
  };

  const handlePayment = async () => {
    if (!selectedSalary) return;

    try {
      await api.post('/payments/process', {
        salary_process_id: selectedSalary.id,
        payment_method: paymentMethod,
      });

      toast.success('Payment processed successfully');
      setDialogOpen(false);
      setSelectedSalary(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process payment');
    }
  };

  const openDialog = (salary) => {
    setSelectedSalary(salary);
    setDialogOpen(true);
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
    </div>;
  }

  return (
    <div className="space-y-6" data-testid="payments-page">
      <div>
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900">Payments</h1>
        <p className="mt-2 text-base text-slate-600">Process approved salary payments</p>
      </div>

      {salaries.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-12 text-center shadow-sm">
          <CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-900">No pending payments</h3>
          <p className="text-slate-600 mt-2">All approved salaries have been paid.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {salaries.map((salary) => (
            <div
              key={salary.id}
              className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm transition-smooth card-hover"
              data-testid={`payment-card-${salary.id}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-3">
                    <h3 className="text-xl font-semibold text-slate-900">{salary.employee_name}</h3>
                    <span className="px-3 py-1 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full text-xs font-semibold">
                      CEO Approved
                    </span>
                    <span className="px-3 py-1 bg-blue-50 text-blue-700 border border-blue-200 rounded-full text-xs font-semibold">
                      {salary.month}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-4">
                    <div>
                      <p className="text-xs font-medium uppercase tracking-wider text-slate-500">Gross Salary</p>
                      <p className="text-base font-mono font-semibold text-slate-900 mt-1">
                        ₹{Math.round(parseFloat(salary.gross_salary)).toLocaleString('en-IN')}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs font-medium uppercase tracking-wider text-slate-500">Total Deductions</p>
                      <p className="text-base font-mono font-semibold text-rose-600 mt-1">
                        -₹{Math.round(parseFloat(salary.total_deductions)).toLocaleString('en-IN')}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs font-medium uppercase tracking-wider text-slate-500">Net Payable</p>
                      <p className="text-lg font-mono font-bold text-emerald-600 mt-1">
                        ₹{Math.round(parseFloat(salary.net_salary)).toLocaleString('en-IN')}
                      </p>
                    </div>
                  </div>

                  <div className="mt-4 p-3 bg-slate-50 rounded-lg">
                    <p className="text-xs font-medium text-slate-600 mb-2">Statutory Deductions Breakdown</p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                      <div>
                        <span className="text-slate-500">ESI:</span>
                        <span className="ml-1 font-mono font-semibold text-slate-900">₹{salary.deductions.ESI || 0}</span>
                      </div>
                      <div>
                        <span className="text-slate-500">PF:</span>
                        <span className="ml-1 font-mono font-semibold text-slate-900">₹{salary.deductions.PF || 0}</span>
                      </div>
                      <div>
                        <span className="text-slate-500">PT:</span>
                        <span className="ml-1 font-mono font-semibold text-slate-900">₹{salary.deductions.PT || 0}</span>
                      </div>
                      <div>
                        <span className="text-slate-500">Other:</span>
                        <span className="ml-1 font-mono font-semibold text-slate-900">₹{salary.deductions.Other || 0}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex gap-3 mt-6 pt-4 border-t border-slate-200">
                <Button
                  onClick={() => openDialog(salary)}
                  className="bg-indigo-600 hover:bg-indigo-700"
                  data-testid={`process-payment-button-${salary.id}`}
                >
                  <CreditCard className="h-4 w-4 mr-2" />
                  Process Payment
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Process Payment</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {selectedSalary && (
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="text-sm font-semibold text-slate-900">{selectedSalary.employee_name}</p>
                <p className="text-xs text-slate-600 mt-1">{selectedSalary.month}</p>
                <p className="text-2xl font-mono font-bold text-emerald-600 mt-3">
                  ₹{Math.round(parseFloat(selectedSalary.net_salary)).toLocaleString('en-IN')}
                </p>
              </div>
            )}
            <div>
              <Label htmlFor="payment_method">Payment Method</Label>
              <select
                id="payment_method"
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
                className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm ring-offset-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-600 focus-visible:ring-offset-2 mt-2"
                data-testid="payment-method-select"
              >
                <option value="Bank Transfer">Bank Transfer</option>
                <option value="Razorpay">Razorpay</option>
              </select>
            </div>
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-xs font-medium text-amber-900">
                ⚠️ Payment Confirmation
              </p>
              <p className="text-xs text-amber-700 mt-1">
                Please ensure all payment details are correct before processing.
              </p>
            </div>
            <Button
              onClick={handlePayment}
              className="w-full bg-emerald-600 hover:bg-emerald-700"
              data-testid="confirm-payment-button"
            >
              Confirm Payment
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Payments;
